import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Text,
  Input,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  HStack,
  VStack,
  Badge,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton
} from '@chakra-ui/react';
import { getSupabase } from '../components/supabaseClient';
import { BASE_URLS } from './env';
import { normalizeRole } from '../config/roles';

const CreditLimitManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);
  const [defaultCreditLimit, setDefaultCreditLimit] = useState(15); // Default fallback
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    loadUsers();
    loadDefaultCreditLimit();
  }, []);

  const loadDefaultCreditLimit = async () => {
    try {
      const hostname = window.location.hostname;
      const baseUrl = hostname === 'localhost' 
        ? BASE_URLS.local.backend 
        : BASE_URLS.render.backend;
      
      const response = await fetch(`${baseUrl}/api/default-credit-limit/`);
      if (response.ok) {
        const data = await response.json();
        setDefaultCreditLimit(data.default_credit_limit);
      } else {
        // console.warn('Failed to load default credit limit, using fallback value');
      }
    } catch (err) {
      // console.warn('Error loading default credit limit:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const supabase = await getSupabase();
      
      // Get all users with their credit information
      const { data, error } = await supabase
        .from('users')
        .select(`
          id,
          email,
          role,
          credit_limit,
          credits_used_this_month,
          credits_used_all_time,
          last_credit_reset_date
        `)
        .order('email');

      if (error) throw error;

      setUsers(data || []);
    } catch (err) {
      // console.error('Error loading users:', err);
      setError('Failed to load users');
      toast({
        title: 'Error',
        description: 'Failed to load users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const [pendingUpdates, setPendingUpdates] = useState({});

  const updateCreditLimit = async (userId, newLimit) => {
    setUpdating(prev => ({ ...prev, [userId]: true }));
    
    try {
      const supabase = await getSupabase();
      
      const { error } = await supabase
        .from('users')
        .update({ credit_limit: parseFloat(newLimit) })
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { ...user, credit_limit: parseFloat(newLimit) }
          : user
      ));

      // Clear pending update
      setPendingUpdates(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });

      toast({
        title: 'Success',
        description: 'Credit limit updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      // console.error('Error updating credit limit:', err);
      toast({
        title: 'Error',
        description: 'Failed to update credit limit',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreditLimitChange = (userId, value) => {
    // Store the pending update without saving to database
    setPendingUpdates(prev => ({ ...prev, [userId]: value }));
    
    // Update local state immediately for UI responsiveness
    // Don't parse as float here - keep as string during typing
    setUsers(prev => prev.map(user => 
      user.id === userId 
        ? { ...user, credit_limit: value }
        : user
    ));
  };

  const handleCreditLimitBlur = (userId) => {
    const pendingValue = pendingUpdates[userId];
    if (pendingValue !== undefined) {
      updateCreditLimit(userId, pendingValue);
    }
  };

  const handleCreditLimitKeyPress = (userId, event) => {
    if (event.key === 'Enter') {
      const pendingValue = pendingUpdates[userId];
      if (pendingValue !== undefined) {
        updateCreditLimit(userId, pendingValue);
      }
    }
  };

  const resetUserCredits = async (userId) => {
    setUpdating(prev => ({ ...prev, [userId]: true }));
    
    try {
      const supabase = await getSupabase();
      
      // First, get current user data to add monthly credits to all-time
      const { data: userData, error: fetchError } = await supabase
        .from('users')
        .select('credits_used_this_month, credits_used_all_time')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;

      const currentMonthly = userData.credits_used_this_month || 0;
      const currentAllTime = userData.credits_used_all_time || 0;
      const newAllTime = currentAllTime + currentMonthly;

      // Update: add monthly credits to all-time, then reset monthly
      const { error } = await supabase
        .from('users')
        .update({ 
          credits_used_all_time: newAllTime,
          credits_used_this_month: 0.0,
          last_credit_reset_date: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId 
          ? { 
              ...user, 
              credits_used_all_time: newAllTime,
              credits_used_this_month: 0.0,
              last_credit_reset_date: new Date().toISOString()
            }
          : user
      ));

      toast({
        title: 'Success',
        description: 'User monthly credits reset successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      // console.error('Error resetting user credits:', err);
      toast({
        title: 'Error',
        description: 'Failed to reset user credits',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const resetToDefault = async (userId) => {
    setUpdating(prev => ({ ...prev, [userId]: true }));
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('users')
        .update({
          credit_limit: null  // This will make it use the default
        })
        .eq('id', userId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `User credit limit reset to default (${defaultCreditLimit})`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      loadUsers();
    } catch (err) {
      // console.error('Error resetting to default:', err);
      toast({
        title: 'Error',
        description: 'Failed to reset to default',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  const resetAllToDefault = async () => {
    onClose(); // Close the modal
    setUpdating(prev => ({ ...prev, all: true }));
    try {
      const supabase = await getSupabase();
      
      // Update each user individually since Supabase requires WHERE clauses
      const updatePromises = users.map(user => 
        supabase
          .from('users')
          .update({ credit_limit: null })
          .eq('id', user.id)
      );
      
      const results = await Promise.all(updatePromises);
      
      // Check if any updates failed
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to update ${errors.length} users`);
      }

      toast({
        title: 'Success',
        description: `All ${users.length} users reset to default credit limit (${defaultCreditLimit})`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      loadUsers();
    } catch (err) {
      // console.error('Error resetting all to default:', err);
      toast({
        title: 'Error',
        description: 'Failed to reset all users to default',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(prev => ({ ...prev, all: false }));
    }
  };

  const getUsageColor = (used, limit) => {
    const percentage = (used / limit) * 100;
    if (percentage >= 90) return 'red';
    if (percentage >= 75) return 'orange';
    if (percentage >= 50) return 'yellow';
    return 'green';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="xl" />
        <Text mt={4}>Loading users...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6}>Credit Limit Manager</Heading>
      
      {/* Display current default credit limit */}
      <Alert status="info" mb={4}>
        <AlertIcon />
        <Box>
          <AlertTitle>Default Credit Limit</AlertTitle>
          <AlertDescription>
            Current default credit limit: <strong>{defaultCreditLimit}</strong> credits
          </AlertDescription>
        </Box>
      </Alert>
      
      <VStack spacing={4} align="stretch" mb={6}>
                 <Text fontSize="sm" color="gray.600">
           Manage monthly credit limits for all users. Users get the specified number of credits per month. Changes take effect immediately.
         </Text>
        
        <HStack spacing={3} alignSelf="flex-start">
          <Button onClick={loadUsers} size="sm">
            Refresh Data
          </Button>
          <Button 
            onClick={onOpen} 
            size="sm" 
            colorScheme="blue"
            isLoading={updating.all}
          >
            Reset All to Default limit: ({defaultCreditLimit})
          </Button>
        </HStack>
      </VStack>

      <Box overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
                          <Tr>
                <Th>User Email</Th>
                <Th>Role</Th>
                <Th>Credit Limit</Th>
                <Th>Monthly Used</Th>
                <Th>Last Reset</Th>
                <Th>Actions</Th>
              </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.id}>
                <Td fontSize="sm">{user.email}</Td>
                <Td>
                  <Badge colorScheme={normalizeRole(user.role) === 'admin' ? 'purple' : 'blue'}>
                    {user.role}
                  </Badge>
                </Td>
                <Td>
                                     <NumberInput
                     size="sm"
                     min={0.01}
                     max={1000}
                     step={1}
                     value={String(user.credit_limit || defaultCreditLimit)}
                     onChange={(value) => handleCreditLimitChange(user.id, value)}
                     onBlur={() => handleCreditLimitBlur(user.id)}
                     onKeyPress={(e) => handleCreditLimitKeyPress(user.id, e)}
                     isDisabled={updating[user.id]}
                   >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Td>
                                 <Td>
                                       <Badge colorScheme={getUsageColor(user.credits_used_this_month || 0, parseFloat(user.credit_limit) || defaultCreditLimit)}>
                      {(user.credits_used_this_month || 0).toFixed(2)} / {parseFloat(user.credit_limit || defaultCreditLimit).toFixed(2)}
                   </Badge>
                 </Td>
                <Td fontSize="xs">
                  {user.last_credit_reset_date ? formatDate(user.last_credit_reset_date) : 'Never'}
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      colorScheme="orange"
                      onClick={() => resetUserCredits(user.id)}
                      isLoading={updating[user.id]}
                    >
                      Reset Credits
                    </Button>
                    <Button
                      size="xs"
                      colorScheme="blue"
                      onClick={() => resetToDefault(user.id)}
                      isLoading={updating[user.id]}
                    >
                      Reset to Default
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Text fontSize="sm" color="gray.600" mt={4}>
        Showing {users.length} users
      </Text>

      {/* Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Reset All to Default</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to reset all users' credit limits to the default value of <strong>{defaultCreditLimit}</strong> credits?
            </Text>
            <Text mt={2} fontSize="sm" color="gray.600">
              This will affect {users.length} users.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={resetAllToDefault}
              isLoading={updating.all}
            >
              Reset All Users
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CreditLimitManager;
